import React from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import {
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
} from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";
import type { DashboardSummary } from "../types/dashboard.types";
import type { SetupStep } from "../setupSteps";
import EarningsHero from "./EarningsHero";
import MobileClientsTrend from "./MobileClientsTrend";
import MobileConversionCard from "./MobileConversionCard";
import MobileWelcome from "./MobileWelcome";
import RecentClientCards from "./RecentClientCards";
import UpgradeBanner from "./UpgradeBanner";
import type { TermCountdown } from "../../billing/termCountdown";
import type { AccessLapse } from "../../billing/accessLapse";

/**
 * The corner and the lift every card on this screen wears.
 *
 * Both come from `card-surface`, which is the app's own card now rather than
 * this screen's: an 8px corner where a desktop card takes 6px, and a hairline
 * shadow under the hairline border. That pairing is the whole of what makes a
 * stack of panels read as an app rather than as a web page narrowed: on a phone
 * the cards are the only shape on the screen, edge to edge with 16px of page
 * either side, and a corner a touch softer than the desktop's is enough to
 * separate them. The page under them is barely off-white, so the border is
 * still what draws the edge and the shadow only lifts it. The blocks that
 * stand in for the cards while the dashboard loads take the same corner from
 * `rounded-card`, so nothing changes shape as it lands.
 */
const CARD = "card-surface border border-line bg-white";

/** One of the two headline counts, as `DashboardPage` works them out. Both
 *  trees are handed the same list, so a tile cannot end up labelled one thing
 *  on a phone and another on a desktop. */
export interface DashboardStat {
  id: string;
  label: string;
  value: number | string;
  emptyMessage?: string | null;
}

/** A compact headline count. The desktop's `StatTile` is 120px tall for one
 *  number, which on a phone is a third of the fold spent on two figures — this
 *  is the same tile with the air taken out, and the figure at the foot of it so
 *  the pair line up along their numbers rather than along their labels. */
const MiniTile: React.FC<DashboardStat & { phase: LoadPhase }> = ({
  label,
  value,
  emptyMessage = null,
  phase,
}) =>
  phase !== "ready" ? (
    <SkeletonCard phase={phase} className="h-[88px] rounded-card" />
  ) : (
    <section className={`h-[88px] p-3.5 flex flex-col justify-between ${CARD}`}>
      <p className="text-[12px] leading-tight text-ink-muted">{label}</p>
      {emptyMessage ? (
        <p className="text-[13px] leading-tight text-ink">{emptyMessage}</p>
      ) : (
        <p className="text-[26px] font-semibold leading-none tracking-tight text-ink">
          {value}
        </p>
      )}
    </section>
  );

/** A band's heading, with the way to the whole of it where there is one. */
const SectionHead: React.FC<{
  title: string;
  to?: string;
  action?: string;
}> = ({ title, to, action }) => (
  <div className="flex items-center justify-between gap-3">
    <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
    {to && (
      <Link
        to={to}
        className="flex-shrink-0 inline-flex items-center gap-0.5 rounded text-[13px] font-medium text-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
      >
        {action}
        <ChevronRight aria-hidden className="w-3.5 h-3.5" />
      </Link>
    )}
  </div>
);

interface MobileDashboardProps {
  summary: DashboardSummary;
  /** The two headline counts, already labelled — see `DashboardStat`. */
  stats: DashboardStat[];
  /** The checklist while any of it is outstanding, `null` otherwise. */
  setup: SetupStep[] | null;
  /** Whether the workspace has nobody on the books, which is what puts every
   *  card on the screen into its first-run state. */
  noClientsYet: boolean;
  /** Whether there is no plan behind the workspace, and what ran out where the
   *  door is shut — the banner's two facts, exactly as the desktop tree gets
   *  them. See `accessLapse`. */
  notSubscribed: boolean;
  /** The last week of a running term, or `null` — see `termCountdown`. The
   *  one state the strip is drawn in for a workspace that is subscribed. */
  ending: TermCountdown | null;
  lapse: AccessLapse;
  /** Stands in for the recent clients on a failure and when there are none. */
  recentEmpty: EmptyStateProps;
  phase: LoadPhase;
}

/**
 * The dashboard as a phone reads it.
 *
 * Not the desktop grid at a narrower width. That screen is six cards laid out to
 * be taken in at once — a greeting, two counts and three charts over three rows —
 * and a phone cannot take anything in at once: it is read a band at a time, top
 * to bottom, usually in a few seconds. So the same facts are ordered by what
 * somebody opening this on a phone came for, and the page carries them rather
 * than a grid of equal cards:
 *
 *   - who they are, as the screen's heading rather than as a card, and under it
 *     either what is left to set up or how the week went. See `MobileWelcome`.
 *   - the offer, for a workspace with no plan behind it. Under the greeting
 *     rather than over it, which is the one thing this screen orders differently
 *     from the desktop: there the strip sits above a card, here it would sit
 *     above the page's own title. It is handed to `MobileWelcome` rather than
 *     drawn beside it, so it lands between the name and the block under it
 *     instead of below both.
 *   - the money, filled and first among the figures — the one place for the eye
 *     to land, and the screen's one hero number.
 *   - the two counts, side by side and compact.
 *   - the two charts worth the scroll, both redrawn for a thumb: the year of
 *     joiners as a curve that can be pressed for a month, and the conversion
 *     split as a meter rather than as the desktop's pie. See
 *     `MobileClientsTrend` and `MobileConversionCard`.
 *   - the newest clients as cards, with the way to the rest beside the heading.
 *
 * Everything below `md`, which is the line `useIsMobile` draws and the same one
 * the shell changes shape at. From `md` up `DashboardPage` renders the tree it
 * always has, untouched — and three of the cards on it (`EarningsCard`,
 * `NewClientsCard`, `TotalClientsCard`) are now only ever drawn there, which is
 * what lets each of them keep the axis, the tooltip and the pie that a pointer
 * can use and a thumb cannot.
 */
const MobileDashboard: React.FC<MobileDashboardProps> = ({
  summary,
  stats,
  setup,
  noClientsYet,
  notSubscribed,
  ending,
  lapse,
  recentEmpty,
  phase,
}) => (
  // `space-y-5` rather than the desktop's 4: down a single column the gap
  // between two cards is the only thing telling them apart, where in a grid the
  // columns do half of that work.
  //
  // The top padding is the air above the greeting, which is now all of it: the
  // shell keeps no bar over the page any more, only the room a notch asks for —
  // see `AppLayout`. The greeting is the screen's own heading and carries the
  // menu button at the end of its line, so this padding is what holds that row
  // off the top of the window.
  <div className="p-page pt-page-top space-y-5">
    <MobileWelcome
      highlights={{
        requests: summary.newRequestsInLast7Days,
        plans: summary.plansCreatedInLast7Days,
        renewals: summary.renewalsInNext7Days,
      }}
      setup={setup}
      banner={
        notSubscribed || ending ? (
          <UpgradeBanner ended={lapse} ending={ending} />
        ) : null
      }
      phase={phase}
    />

    <EarningsHero
      total={summary.totalIncome}
      data={summary.incomeByMonth}
      empty={noClientsYet}
      phase={phase}
    />

    <div className="grid grid-cols-2 gap-3">
      {stats.map((stat) => (
        <MiniTile key={stat.id} {...stat} phase={phase} />
      ))}
    </div>

    <MobileClientsTrend
      data={summary.newClientsByMonth}
      empty={noClientsYet}
      phase={phase}
    />

    <MobileConversionCard
      paidClients={summary.activeClients}
      totalRequests={summary.totalRequests}
      conversionRate={summary.conversionRate}
      empty={noClientsYet}
      phase={phase}
    />

    {/* The heading sits on the page rather than inside a card, and the cards
        under it are the list — the band is a section of the screen rather than
        one more surface with a table in it. "See all" only where there is
        something to see: on a workspace with no clients the empty state below
        already carries the one thing to do. */}
    <div className="space-y-3">
      <SectionHead
        title="Recent clients"
        to={summary.recentClients.length > 0 ? ROUTES.myClients : undefined}
        action="See all"
      />
      <RecentClientCards
        clients={summary.recentClients}
        phase={phase}
        empty={recentEmpty}
      />
    </div>
  </div>
);

export default MobileDashboard;
